import React from 'react';
import { useTranslation } from 'next-i18next';

import { CartSlot } from 'components/cart';
import { PageSeo } from 'components/common';
import { Layout } from 'components/layout';
import { APP_ROUTES } from 'constants/routes';
import { TRANSLATIONS } from 'constants/translations';
import * as SeoHelpers from 'helpers/SeoHelpers';
import { createStaticPaths, prefetchConfig } from 'helpers/ServerHelpers';
import { withSSGPage } from 'hoc/WithServerPage/withSSGPage';
import { prefetchProduct, useProduct } from 'hooks/data/useProduct';
import ProductTemplate from 'templates/Product/ProductTemplate';

import { type NextPageWithLayout, PageType } from 'types';

export const getStaticPaths = createStaticPaths();

export const getStaticProps = withSSGPage(async (context, props) => {

  const { queryClient, slug } = props;
  const productSlug = slug ? slug[slug.length - 1] : '';

  const [product] = await Promise.all([
    prefetchProduct(productSlug, queryClient),
    prefetchConfig(queryClient)
  ]);

  // Check if not found
  if (!product) {
    return { notFound: true };
  }

  // Check if moved
  if (!(typeof product === 'boolean') && product.redirect) {
    return {
      redirect: {
        permanent: true,
        destination: `${APP_ROUTES.PRODUCTS}/${product.redirect}`
      }
    };
  }

  return {
    props: {
      productSlug
    }
  };

}, {
  translations: [TRANSLATIONS.COMMON],
  pageType: PageType.Product,
  revalidate: 10
});

const Page: NextPageWithLayout<PageType.Product, {productSlug: string}> = (props) => {
  const { productSlug } = props;

  const { data: product } = useProduct(productSlug);
  const { t } = useTranslation();

  const pageSeoProps = React.useMemo(() => SeoHelpers.getProductPageSeoProps(t, product), [t, product]);

  return (
    <>
      <PageSeo {...pageSeoProps} />
      <ProductTemplate slug={productSlug} />
    </>
  );
};

const layoutProps = {
  rightSlot: {
    component: CartSlot,
    props: {}
  },
  header: {
    props: {
      hideCartOnLargeScreens: true
    },
    isVisible: true
  },
  openInAppBanner: {
    isVisible: true,
    props: {
      offset: 81
    }
  }
};

Page.getLayout = (page) => {
  return (
    <Layout {...layoutProps}>
      {page}
    </Layout>
  );
};

export default Page;
